import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  inject,
  Injector,
  input,
  model,
  output,
  PLATFORM_ID,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isPlatformBrowser } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { INTERNAL_ICONS, TRANSLATION_PREFIX } from '@klubovna/shared-models';
import { SelectOption, SelectValue } from '@klubovna/shared-models';

@Component({
  selector: 'k-select',
  imports: [ TranslateModule ],
  templateUrl: './select.component.html',
  styleUrl: './select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SelectComponent),
      multi: true,
    },
  ],
})
export class SelectComponent implements ControlValueAccessor, AfterViewInit {
  placeholder = input<string>('');
  options = input<SelectOption[]>([]);
  errorMessages = input<Record<string, string>>({});
  required = input<boolean>(false);
  showClearButton = input<boolean>(false);
  searchable = input<boolean>(false);
  searchPlaceholder = input<string>('');
  multiple = input<boolean>(false);
  multipleValues = input<SelectValue[]>([]);
  onValueChange = input<(value: SelectValue | SelectValue[] | null) => void>();

  valueChange = output<SelectValue | SelectValue[] | null>();

  value = model<SelectValue | null>(null);
  internalMultipleValues = signal<SelectValue[]>([]);

  disabled = signal(false);
  isOpen = signal(false);
  isError = signal(false);
  currentErrorMessage = signal<string | null>(null);
  isRequired = signal(false);
  activeIndex = signal<number>(-1);
  searchTerm = signal('');

  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  displayPlaceholder = computed(() => {
    const baseText = this.placeholder();
    const required = this.isRequired();
    return required && baseText ? `${ baseText } *` : baseText;
  });

  filteredOptions = computed(() => {
    const term = this.searchTerm().toLowerCase().trim();
    const allOptions = this.options();

    if (!term) {
      return allOptions;
    }

    return allOptions.filter(option =>
      option.label.toLowerCase().includes(term)
    );
  });

  currentMultipleValues = computed(() => {
    return this.internalMultipleValues();
  });

  selectedOption = computed(() => {
    if (this.multiple()) {
      return null;
    }
    const currentValue = this.value();
    if (currentValue === null) {
      return null;
    }
    return this.options().find((option) => option.value === currentValue) ?? null;
  });

  selectedOptions = computed(() => {
    if (!this.multiple()) {
      return [];
    }
    const values = this.currentMultipleValues();
    return this.options().filter(option => values.includes(option.value));
  });

  selectedLabel = computed(() => {
    if (this.multiple()) {
      const selected = this.selectedOptions();
      if (selected.length === 0) {
        return null;
      }
      if (selected.length === 1) {
        return selected[0].label;
      }
      return this.translate.instant('common.selected_count', { count: selected.length });
    }
    return this.selectedOption()?.label ?? null;
  });

  shouldShowClear = computed(() => {
    if (this.multiple()) {
      return this.showClearButton() && this.currentMultipleValues().length > 0 && !this.disabled();
    }
    return this.showClearButton() && this.value() !== null && !this.disabled();
  });

  effectiveIcon = computed(() => {
    if (this.shouldShowClear()) {
      return INTERNAL_ICONS.CLOSE;
    }
    return this.isOpen() ? INTERNAL_ICONS.EXPAND_LESS : INTERNAL_ICONS.EXPAND_MORE;
  });

  hasSuffixIcon = computed(() => !!this.effectiveIcon());
  isIconClickable = computed(() => this.shouldShowClear());

  private ngControl = signal<NgControl | null>(null);
  private injector = inject(Injector);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private platformId = inject(PLATFORM_ID);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const control = this.ngControl()?.control;
      if (control) {
        this.updateErrorState(control);
      }
    });

    effect(() => {
      const filtered = this.filteredOptions();
      const selectedValue = this.value();
      if (!this.isOpen()) {
        this.activeIndex.set(this.findOptionIndexByValue(filtered, selectedValue));
      }
    });

    effect(() => {
      const externalValues = this.multipleValues();
      this.internalMultipleValues.set([ ...externalValues ]);
    });
  }

  ngAfterViewInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    this.ngControl.set(ngControl);

    if (ngControl?.control) {
      this.updateRequiredState(ngControl.control);

      ngControl.control.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.ngControl.set(this.ngControl());
      });
    }
  }

  private updateRequiredState(control: any): void {
    if (control && control.validator) {
      const validator = control.validator({} as any);
      const hasRequiredValidator = validator && validator.required;
      this.isRequired.set(hasRequiredValidator || this.required());
    } else {
      this.isRequired.set(this.required());
    }
  }

  private onChange = (_value: SelectValue | SelectValue[] | null) => {};
  private onTouched = () => {};

  writeValue(value: SelectValue | SelectValue[] | null): void {
    if (this.multiple()) {
      this.internalMultipleValues.set(Array.isArray(value) ? value : value ? [ value ] : []);
    } else {
      this.value.set(Array.isArray(value) ? value[0] ?? null : value ?? null);
    }
  }

  registerOnChange(fn: (value: SelectValue | SelectValue[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    if (isDisabled) {
      this.close();
    }
  }

  toggleOpen(): void {
    if (this.disabled()) {
      return;
    }

    if (this.isOpen()) {
      this.close();
      return;
    }

    this.open();
  }

  open(): void {
    if (this.disabled()) {
      return;
    }

    this.isOpen.set(true);
    this.searchTerm.set('');
    this.activeIndex.set(this.findBestActiveIndex(this.filteredOptions(), this.value()));

    if (this.searchable() && isPlatformBrowser(this.platformId)) {
      queueMicrotask(() => {
        if (this.searchInput?.nativeElement) {
          this.searchInput.nativeElement.value = '';
          this.searchInput.nativeElement.focus();
        }
      });
    }
  }

  close(): void {
    if (!this.isOpen()) {
      return;
    }

    this.isOpen.set(false);
    this.searchTerm.set('');
  }

  onBlur(): void {
    this.onTouched();
    const control = this.ngControl()?.control;
    if (control) {
      this.updateErrorState(control);
    }
  }

  onSuffixIconClick(event: MouseEvent): void {
    if (!this.isIconClickable()) {
      return;
    }

    event.stopPropagation();
    this.clearValue();
  }

  clearValue(): void {
    if (this.multiple()) {
      this.internalMultipleValues.set([]);
      this.onChange([]);
      this.emitValue([]);
      this.close();
    } else {
      this.value.set(null);
      this.onChange(null);
      this.emitValue(null);
    }
    this.onTouched();

    if (this.isError()) {
      this.isError.set(false);
      this.currentErrorMessage.set(null);
    }
  }

  private emitValue(value: SelectValue | SelectValue[] | null): void {
    this.valueChange.emit(value);
    this.onValueChange()?.(value);
  }

  isOptionSelected(option: SelectOption): boolean {
    if (this.multiple()) {
      return this.currentMultipleValues().includes(option.value);
    }
    return this.value() === option.value;
  }

  onOptionClick(option: SelectOption): void {
    if (option.disabled) {
      return;
    }

    if (this.multiple()) {
      this.toggleMultipleValue(option.value);
    } else {
      this.setValue(option.value);
      this.close();
    }
  }

  private toggleMultipleValue(value: SelectValue): void {
    const current = [ ...this.internalMultipleValues() ];
    const index = current.indexOf(value);

    let newValues: SelectValue[];
    if (index === -1) {
      newValues = [ ...current, value ];
    } else {
      newValues = current.filter(v => v !== value);
    }

    this.internalMultipleValues.set(newValues);
    this.onChange(newValues);
    this.emitValue(newValues);

    if (this.isError()) {
      this.isError.set(false);
      this.currentErrorMessage.set(null);
    }
  }

  onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.searchTerm.set(value);
    this.activeIndex.set(this.findFirstEnabledIndex(this.filteredOptions()));
  }

  onSearchKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.moveActive(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.moveActive(-1);
        break;
      case 'Enter':
        event.preventDefault();
        this.selectActiveOption();
        break;
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'Tab':
        this.close();
        break;
    }
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) {
      return;
    }

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (this.isOpen()) {
          this.selectActiveOption();
        } else {
          this.open();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!this.isOpen()) {
          this.open();
        } else {
          this.moveActive(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!this.isOpen()) {
          this.open();
        } else {
          this.moveActive(-1);
        }
        break;
      case 'Home':
        if (this.isOpen()) {
          event.preventDefault();
          this.activeIndex.set(this.findFirstEnabledIndex(this.filteredOptions()));
        }
        break;
      case 'End':
        if (this.isOpen()) {
          event.preventDefault();
          this.activeIndex.set(this.findLastEnabledIndex(this.filteredOptions()));
        }
        break;
      case 'Escape':
        if (this.isOpen()) {
          event.preventDefault();
          this.close();
        }
        break;
      case 'Backspace':
      case 'Delete':
        if (this.shouldShowClear()) {
          event.preventDefault();
          this.clearValue();
        }
        break;
      default:
        break;
    }
  }

  private moveActive(direction: 1 | -1): void {
    const options = this.filteredOptions();
    const currentIndex = this.activeIndex();
    const nextIndex =
      direction === 1
        ? this.findNextEnabledIndex(options, currentIndex)
        : this.findPrevEnabledIndex(options, currentIndex);
    this.activeIndex.set(nextIndex);
  }

  private selectActiveOption(): void {
    const index = this.activeIndex();
    const options = this.filteredOptions();
    const option = options[index];
    if (!option || option.disabled) {
      return;
    }

    if (this.multiple()) {
      this.toggleMultipleValue(option.value);
    } else {
      this.setValue(option.value);
      this.close();
    }
  }

  private setValue(value: SelectValue): void {
    this.value.set(value);
    this.onChange(value);
    this.onTouched();
    this.emitValue(value);

    if (this.isError()) {
      this.isError.set(false);
      this.currentErrorMessage.set(null);
    }
  }

  @HostListener('document:click', [ '$event' ])
  onDocumentClick(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (!this.isOpen()) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.close();
    }
  }

  private updateErrorState(control: any): void {
    const hasError = control.invalid && control.touched;
    this.isError.set(hasError);

    if (hasError && control.errors) {
      const errorMessage = this.getErrorMessage(control.errors);
      this.currentErrorMessage.set(errorMessage);
    } else {
      this.currentErrorMessage.set(null);
    }
  }

  private getErrorMessage(errors: any): string | null {
    const errorKeys = Object.keys(errors);
    const firstErrorKey = errorKeys[0];

    if (firstErrorKey && this.errorMessages()[firstErrorKey]) {
      const translationKey = this.errorMessages()[firstErrorKey];
      return `${ TRANSLATION_PREFIX }${ translationKey }`;
    }

    if (firstErrorKey) {
      return `${ TRANSLATION_PREFIX }${ firstErrorKey }`;
    }

    return null;
  }

  private findOptionIndexByValue(options: SelectOption[], value: SelectValue | null): number {
    if (value === null) {
      return -1;
    }
    return options.findIndex((option) => option.value === value);
  }

  private findBestActiveIndex(options: SelectOption[], selectedValue: SelectValue | null): number {
    const selectedIndex = this.findOptionIndexByValue(options, selectedValue);
    if (selectedIndex >= 0 && !options[selectedIndex]?.disabled) {
      return selectedIndex;
    }
    return this.findFirstEnabledIndex(options);
  }

  private findFirstEnabledIndex(options: SelectOption[]): number {
    return options.findIndex((option) => !option.disabled);
  }

  private findLastEnabledIndex(options: SelectOption[]): number {
    for (let i = options.length - 1; i >= 0; i -= 1) {
      if (!options[i]?.disabled) {
        return i;
      }
    }
    return -1;
  }

  private findNextEnabledIndex(options: SelectOption[], currentIndex: number): number {
    if (options.length === 0) {
      return -1;
    }

    const startIndex = Math.max(-1, currentIndex);
    for (let i = startIndex + 1; i < options.length; i += 1) {
      if (!options[i]?.disabled) {
        return i;
      }
    }
    return this.findFirstEnabledIndex(options);
  }

  private findPrevEnabledIndex(options: SelectOption[], currentIndex: number): number {
    if (options.length === 0) {
      return -1;
    }

    const startIndex = currentIndex < 0 ? options.length : currentIndex;
    for (let i = startIndex - 1; i >= 0; i -= 1) {
      if (!options[i]?.disabled) {
        return i;
      }
    }
    return this.findLastEnabledIndex(options);
  }
}
